import { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import LoggingAPI from "@/api/logging";
import RelayAPI from "@/api/RelayAPI";
import { Button, Modal, TextInput } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { useRelayInfo } from "../../../hooks";
import { useForm } from "@mantine/form";

interface InviteUserModalProps {
  show: boolean;
  onClose?: () => void;
  onSubmit?: (defaultInfo?: any) => void;
  onCancel?: () => void;
  buttonProps?: {
    cancelText: string;
    submitText: string;
  };
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps,
}) => {
  const { relayInfo } = useRelayInfo();
  const [submitting, setSubmitting] = useState(false);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const form = useForm({
    initialValues: {
      email: "",
    },
    validate: (values) => {
      const errors: { email?: string } = {};

      if (!values.email) {
        errors.email = "This field is required";
      }

      return errors;
    },
  });

  if (!relayInfo) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const validationResults = form.validate();

      if (validationResults.hasErrors) {
        return;
      }

      const { email } = form.getValues();

      await RelayAPI.inviteUser({
        email,
        tenant_uuid: relayInfo.tenant_uuid,
        tenant_admin_email: currentUser.email,
        type: "invite",
        created_on: new Date().toISOString(),
      });

      enqueueSnackbar("User invited", { variant: "success" });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Invited Relay User: ${email}`,
        event: "relay:invite_user",
        object_type: "relay",
      });

      onSubmit?.({ email });
    } catch (error) {
      console.error("Error inviting user:", error);
      enqueueSnackbar(`Error inviting user.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }

    form.reset();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Invite User to Relay</Modal.Title>
      <div style={{ fontWeight: 500, margin: "20px 0px" }}>
        Enter the email of the user you would like to invite to your Relay
        tenant.
      </div>
      <div>
        <TextInput
          size="sm"
          placeholder="Email"
          label="Email"
          key={form.key("email")}
          {...form.getInputProps("email")}
        />
      </div>
      <ButtonMenu style={{ marginTop: 20 }}>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Invite User"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
